import { createSelector } from "reselect";
import { CANVAS_PX_PRECISION } from "../../Constants";
import { SelectionConstants } from "../../store/reducers/index";
import { getStore } from "../../store/ReduxRegistry";
import { IAnyObjectType } from "../components/SelectionBox";
import { CanvasMode } from "../interfaces/model/enumerations/index";
import { ICanvasDimension, ICanvasEditorState } from "../interfaces/model/index";
import { IPosition } from "../meta/Drag/CsiDragController";
import { getSelectedPage } from "./index";

/** context type from state */
const _contextType = (state: ICanvasEditorState): string => state.canvasEditor.contextType as string;

/** froala license from state */
const _froalaLicense = (state: ICanvasEditorState): string => state.canvasEditor.froalaLicense as string;

/** Snap to grid option. Set by user. */
const _isSnapToGrid = (state: ICanvasEditorState): boolean => state.canvasEditor.isSnapToGrid;

/** If text mode is selected then set textShape defaults otherwise shape defaults */
const _getSetDefaultActionType = (state: ICanvasEditorState): string => {
	const isTextMode = state.selection.present.shapeDrawTools && state.selection.present.shapeDrawTools.isTextShape;
	const actionType = isTextMode ? SelectionConstants.SET_TEXT_SHAPE_DEFAULT_PROPERTIES :
		SelectionConstants.SET_SHAPE_DEFAULT_PROPERTIES;
	return actionType;
};

/** Zoom factor */
const _zoomFactor = (state: ICanvasEditorState): number => state.canvasEditor.zoomFactor;

/** Canvas Dimensions */
const _canvasDimension = (state: ICanvasEditorState): ICanvasDimension => {
	return { editorWidth: state.canvasEditor.editorWidth, editorHeight: state.canvasEditor.editorHeight };
};

/** Grid constant */
const _girdConstant = (state: ICanvasEditorState): number => state.canvasEditor.gridInc;

/** Format painter active */
const _formatPainterActive = (state: ICanvasEditorState): boolean => state.canvasEditor.isFormatPainterActive;

/** Current selected page number */
const _selectedPageNumber = (state: ICanvasEditorState): number => state.selection.present.selectedPage as number;

/** SnapXToGuide flag */
const _snapXToGuides = (state: ICanvasEditorState): boolean => state.guides.snapXToGuides;

/** SnapYToGuide flag */
const _snapYToGuides = (state: ICanvasEditorState): boolean => state.guides.snapYToGuides;

/** Canvas Mode */
const _canvasMode = (state: ICanvasEditorState): CanvasMode => state.canvasEditor.mode;

/** state maintained constants selector */
const _stateConstants = createSelector([
	_contextType,
	_froalaLicense,
	_isSnapToGrid,
	_getSetDefaultActionType,
	_zoomFactor,
	_canvasDimension,
	_girdConstant,
	_snapXToGuides,
	_snapYToGuides,
	_selectedPageNumber,
	_formatPainterActive,
	_canvasMode,
], (
	contextType: string,
	license: string,
	isSnapToGrid: boolean,
	getSetDefaultActionType: string,
	zoomFactor: number,
	canvasDimension: ICanvasDimension,
	gridInc: number,
	snapXToGuides: boolean,
	snapYToGuides: boolean,
	selectedPageNumber: number,
	isFormatPainterActive: boolean,
	canvasMode: CanvasMode,
) => ({
	contextType,
	license,
	isSnapToGrid,
	getSetDefaultActionType,
	zoomFactor,
	canvasDimension,
	gridInc,
	snapXToGuides,
	snapYToGuides,
	selectedPageNumber,
	isFormatPainterActive,
	canvasMode,
}));

/**
 * Expose state constants selector value.
 * Used to access constants maintained in redux state.
 * Store is neither exposed to outside nor passed to state aware utility.
 */
export const  stateConstants = () => _stateConstants(getStore()
	.getState() as ICanvasEditorState);

/** Calculate zoom value for give value */
const _getValue = (value: number) => value;

/** Return position */
const _getPosition = (position: IPosition) => position;

/**
 * Get zoom value selector
 */
export const getZoomValue = createSelector([_getValue, stateConstants], ( value: number, stateConstant: IAnyObjectType) => {
	return value / stateConstant.zoomFactor;
});

/**
 * Get snapped values
 */
export const getSnapValue = createSelector([_getValue, stateConstants], ( value: number, stateConstant: IAnyObjectType) => {
	const gridInc = stateConstant.gridInc;
	return stateConstant.isSnapToGrid ? Math.round(value / gridInc) * gridInc : value;
});

export const getSnapPosition = createSelector([_getPosition, stateConstants], ( point: IPosition, stateConstant: IAnyObjectType) => {
	const { gridInc } = stateConstant;
	if (stateConstant.isSnapToGrid) {
		return {
			x: Math.round(point.x / gridInc) * gridInc,
			y: Math.round(point.y / gridInc) * gridInc,
		};
	} else {
		return point;
	}
});

/**
 * Get position as per zoom factor set.
 * @param position {IPosition}
 */
export const getZoomPosition = createSelector([_getPosition], ( position: IPosition) => {
	const newPosition: IPosition = {
		x : getZoomValue(position.x),
		y : getZoomValue(position.y),
	};
	return newPosition;
});

/**
 * Get value with Zoom and Snap to grid applied.
 * @param value {number}
 */
export const getZoomNGridValue = createSelector([_getValue, stateConstants], (value: number, stateConstant: IAnyObjectType) => {
	const isSnapToGrid = stateConstant.isSnapToGrid;
	const gridInc = isSnapToGrid ? stateConstant.gridInc : 1;
	const snapValue = Math.round(value / gridInc) * gridInc;
	return getZoomValue(snapValue);
});

/**
 * Get value with Snap to grid applied.
 * @param value {number}
 */
export const getSnapToGridValue = createSelector([_getValue, stateConstants], (value: number, stateConstant: IAnyObjectType) => {
	const isSnapToGrid = stateConstant.isSnapToGrid;
	const gridInc = isSnapToGrid ? stateConstant.gridInc : 1;
	const snapValue = Math.round(value / gridInc) * gridInc;
	return snapValue;
});

/**
 * Get current selected page id
 */
export const getSelectedPageId = () => {
	const state = getStore()
		.getState() as ICanvasEditorState;
	const page = getSelectedPage(state);
	return page && page.id;
};
